import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorText } from "@/lib/api";
import { Field, TextField, PrimaryButton, GhostButton } from "./fields";
import { Switch } from "@/components/ui/switch";
import ImageField from "./ImageField";

const EMPTY_THEME = { heading_font: "", body_font: "", heading_uppercase: false, ink: "", accent: "", surface: "" };
const EMPTY = { name: "", slug: "", origin: "", tagline: "", description: "", logo: "", logo_light: "", logo_class: "", wordmark: "", hero_image: "", order: 0, visible: true, theme: EMPTY_THEME };

export default function BrandForm({ open, onClose, brand, onSaved }) {
  const [form, setForm] = useState(brand ? { ...EMPTY, ...brand, theme: { ...EMPTY_THEME, ...(brand.theme || {}) } } : EMPTY);
  const setTheme = (k) => (v) => setForm((f) => ({ ...f, theme: { ...f.theme, [k]: v } }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, order: Number(form.order) || 0 };
    delete payload.id;
    delete payload.model_count;
    try {
      if (brand?.id) await api.put(`/admin/brands/${brand.id}`, payload);
      else await api.post("/admin/brands", payload);
      toast.success(brand ? "Brand updated." : "Brand added.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorText(err, "Could not save brand."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-navy-ink/10 bg-navy sm:max-w-2xl" data-testid="brand-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light text-navy-ink">{brand ? `Edit ${brand.name}` : "Add a brand partner"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-6" data-testid="brand-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField label="Brand name" required value={form.name} onChange={set("name")} testId="brand-form-name" placeholder="Sunseeker" />
            <TextField label="URL slug" hint="Leave blank to generate from the name." value={form.slug} onChange={set("slug")} testId="brand-form-slug" placeholder="sunseeker" />
            <TextField label="Origin" value={form.origin} onChange={set("origin")} testId="brand-form-origin" placeholder="Poole, England" />
            <TextField label="Display order" type="number" value={form.order} onChange={set("order")} testId="brand-form-order" />
            <Field label="Visible on website" hint="Off = brand, its hero slide, page and all models are hidden from visitors but kept intact.">
              <div className="flex h-11 items-center"><Switch checked={form.visible !== false} onCheckedChange={set("visible")} data-testid="brand-form-visible" /></div>
            </Field>
          </div>
          <TextField label="Tagline" value={form.tagline} onChange={set("tagline")} testId="brand-form-tagline" placeholder="The definitive British motor yacht." />
          <TextField label="Description" multiline value={form.description} onChange={set("description")} testId="brand-form-description" />
          <ImageField label="Hero image" value={form.hero_image} onChange={set("hero_image")} testId="brand-form-hero" />
          <ImageField label="Logo (dark version, for light backgrounds)" value={form.logo} onChange={set("logo")} testId="brand-form-logo" aspect="aspect-[3/1]" />
          <ImageField label="Logo (white version, for photo/dark backgrounds)" value={form.logo_light} onChange={set("logo_light")} testId="brand-form-logo-light" aspect="aspect-[3/1]" />
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField label="Text wordmark (if no logo)" value={form.wordmark} onChange={set("wordmark")} testId="brand-form-wordmark" placeholder="FERRETTI" />
            <TextField label="Logo scale class" hint="Optional Tailwind class, e.g. scale-150" value={form.logo_class} onChange={set("logo_class")} testId="brand-form-logo-class" />
          </div>
          <Field label="Brand theme (optional — per shipyard guidelines)" hint="Applied on this brand's page and its model pages. Leave blank to use the Modi Marine house style.">
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField label="Heading font stack" value={form.theme.heading_font} onChange={setTheme("heading_font")} testId="brand-form-theme-heading-font" placeholder="'GT Flexa Expanded', 'Zalando Sans Expanded', sans-serif" />
              <TextField label="Body font stack" value={form.theme.body_font} onChange={setTheme("body_font")} testId="brand-form-theme-body-font" placeholder="'Zalando Sans SemiExpanded', sans-serif" />
              <TextField label="Ink (heading colour)" value={form.theme.ink} onChange={setTheme("ink")} testId="brand-form-theme-ink" placeholder="#383131" />
              <TextField label="Accent colour" value={form.theme.accent} onChange={setTheme("accent")} testId="brand-form-theme-accent" placeholder="#A8998B" />
              <TextField label="Surface colour" value={form.theme.surface} onChange={setTheme("surface")} testId="brand-form-theme-surface" placeholder="#EFE9DD" />
              <label className="flex items-center gap-3 pt-6 text-xs text-navy-ink">
                <Switch checked={!!form.theme.heading_uppercase} onCheckedChange={setTheme("heading_uppercase")} data-testid="brand-form-theme-uppercase" />
                Uppercase headings
              </label>
            </div>
          </Field>
          <div className="flex justify-end gap-3 border-t border-navy-ink/10 pt-6">
            <GhostButton type="button" onClick={onClose} data-testid="brand-form-cancel">Cancel</GhostButton>
            <PrimaryButton type="submit" disabled={busy} data-testid="brand-form-submit">{busy ? "Saving…" : "Save brand"}</PrimaryButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
